import { useEffect, useMemo } from 'react';
import { InboxView, ListThreadsFilter } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { isEqual } from 'lodash-es';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useDebouncedValue } from '@frontend/timer';

/**
 * Normalizes the request parameters for thread count and list queries.
 * Ensures all array fields are sorted and default values are set for comparison.
 *
 * @param locationIds - The location IDs to include in the request.
 * @param filter - A filter object for listing threads.
 * @returns A normalized object containing sorted arrays and defaulted filter fields with the following properties:
 *   - locationIds: Sorted array of location IDs.
 *   - filter: A filter object with sorted arrays and default values.
 */
const normalizeRequest = (
  locationIds: string[] | undefined = [],
  filter: ListThreadsFilter | undefined
): { locationIds: string[]; filter: ListThreadsFilter } => {
  // TODO: improve the type handling of this function and use `undefined` as fallback for empty arrays instead of the
  // other way around
  return {
    locationIds: [...(locationIds ?? [])].sort(),
    filter: {
      departmentIds: [...(filter?.departmentIds ?? [])].sort(),
      personPhones: [...(filter?.personPhones ?? [])].sort(),
      locationPhones: [...(filter?.locationPhones ?? [])].sort(),
      statuses: [...(filter?.statuses ?? [])].sort(),
      inboxView: filter?.inboxView ?? InboxView.UNSPECIFIED,
      messageTags: [...(filter?.messageTags ?? [])].sort(),
      excludeAutomatedLastMessage: filter?.excludeAutomatedLastMessage ?? false,
      personIds: [...(filter?.personIds ?? [])].sort(),
    },
  };
};

/**
 * React hook to align the count of threads with the actual list of threads in cache.
 *
 * This hook compares the cached count data (CountThreads) with the paginated thread list data (ListThreads) for
 * the same query parameters. If a discrepancy is detected for an extended period (debounced), the count cache
 * is forcibly updated to match the list. This will help ensure that the count displayed in the UI matches the list of
 * threads displayed as a back-up way of keeping the count in sync with the list of threads.
 *
 * @param debounceTime - Time in milliseconds to debounce updates (default: 2000).
 */
export const useAlignCountAndListV4 = (debounceTime = 2000) => {
  const { getQueriesData, updateQuery } = SMSDataV4._QueryUpdaters.useQueryUpdaters();

  const countQueries = getQueriesData({
    endpointName: 'CountThreads',
  });

  const countQueriesToUpdate = useMemo(() => {
    return countQueries.reduce<{ queryKey: (typeof countQueries)[number][0]; newCount: number }[]>(
      (acc, countQuery) => {
        const countRequest = countQuery[0][2];
        const normalizedCountRequest = normalizeRequest(countRequest.locationIds, countRequest.filter);

        const associatedListThreadsQueries = getQueriesData<'ListThreads', true>({
          endpointName: 'ListThreads',
          queryFilters: {
            predicate: (query) => {
              const listRequest = query.queryKey[2];
              const normalizedListRequest = normalizeRequest(listRequest.locationIds, listRequest.filter);

              return isEqual(normalizedCountRequest, normalizedListRequest);
            },
          },
        });

        const listThreadsCount = associatedListThreadsQueries.reduce<number | undefined>((acc, [, queryData]) => {
          if (acc === -1) return acc;
          const hasLastPage = queryData?.pages.some((page) => !page.olderPageToken);
          if (!hasLastPage) {
            return acc;
          }

          const count = queryData?.pages.reduce((sum, page) => sum + (page.threads?.length ?? 0), 0) ?? 0;
          if (acc === undefined) {
            return count;
          } else if (acc !== count) {
            return -1;
          }

          return count;
        }, undefined);

        const countData = countQuery[1];
        if (countData && listThreadsCount === countData.count) {
          return acc;
        }

        if (listThreadsCount === undefined || listThreadsCount === -1) {
          return acc;
        }

        acc.push({
          queryKey: countQuery[0],
          newCount: listThreadsCount,
        });
        return acc;
      },
      []
    );
    // use JSON.stringify because the `countQueries` value is not referentially stable since it's recalculated whenever
    // `queryClient` changes
  }, [JSON.stringify(countQueries), getQueriesData]);

  // If the count query doesn't match the list threads query for a certain amount of time, update the count query
  const { debouncedValue: debouncedCountQueriesToUpdate } = useDebouncedValue(countQueriesToUpdate, debounceTime);
  useEffect(() => {
    debouncedCountQueriesToUpdate.forEach(({ queryKey, newCount }) => {
      updateQuery({
        endpointName: 'CountThreads',
        queryFilters: {
          queryKey,
          exact: true,
        },
        updater: () => ({
          count: newCount,
        }),
      });
    });
  }, [debouncedCountQueriesToUpdate]);
};
